import { memo, useState } from 'react';

// material-ui
import { Theme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';

// project imports
import NavCollapse from './NavCollapse';
import NavGroup from './NavGroup';
import { MenuOrientation, HORIZONTAL_MAX_ITEM } from 'config';
import useConfig from 'hooks/useConfig';
import { useGetMenuMaster } from 'api/menu';
import { useDynamicMenu } from 'hooks/useDynamicMenu';

// types
import { NavItemType } from 'types';

// ==============================|| SIDEBAR MENU LIST ||============================== //

function MenuList() {
  const downMD = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

  const {
    state: { menuOrientation }
  } = useConfig();

  const { menuMaster } = useGetMenuMaster();
  const drawerOpen = menuMaster.isDashboardDrawerOpened;
  const isHorizontal = menuOrientation === MenuOrientation.HORIZONTAL && !downMD;

  const [selectedID, setSelectedID] = useState<string | undefined>('');

  // Single source of truth: dynamic menu from the API.
  // menuItems reference is stable (memoized inside useDynamicMenu).
  const { menuItems, isLoading } = useDynamicMenu();

  if (isLoading || menuItems.length === 0) {
    return null;
  }

  // Horizontal mode: cap the number of visible top-level items
  const lastItem = isHorizontal ? HORIZONTAL_MAX_ITEM : null;

  let lastItemIndex = menuItems.length - 1;
  let remItems: NavItemType[] = [];
  let lastItemId: string = '';

  if (lastItem && lastItem < menuItems.length) {
    lastItemId = menuItems[lastItem - 1].id!;
    lastItemIndex = lastItem - 1;
    remItems = menuItems.slice(lastItem - 1).map((item) => ({
      title: item.title,
      elements: item.children,
      icon: item.icon,
      ...(item.url && { url: item.url })
    }));
  }

  const navItems = menuItems.slice(0, lastItemIndex + 1).map((item) => {
    if (item.type === 'collapse') {
      return (
        <Box key={item.id} component="div">
          <NavCollapse menu={item} level={1} parentId={item.id!} />
          <Divider sx={{ mt: 0.25, mb: 1.25 }} />
        </Box>
      );
    }

    if (item.type === 'group') {
      return (
        <NavGroup
          key={item.id}
          setSelectedID={setSelectedID}
          selectedID={selectedID}
          item={item}
          lastItem={lastItem!}
          remItems={remItems}
          lastItemId={lastItemId}
        />
      );
    }

    return (
      <Typography key={item.id} variant="h6" align="center" sx={{ color: 'error.main' }}>
        Menu Items Error
      </Typography>
    );
  });

  return !isHorizontal ? (
    <Box {...(drawerOpen && { sx: { mt: 1.5 } })}>{navItems}</Box>
  ) : (
    <>{navItems}</>
  );
}

export default memo(MenuList);
